/**
 * AiPhotoEstimator — lead-magnet modal.
 *
 * 3-step flow:
 *   1. Upload photo + service selection + basic info
 *   2. AI analysis (Gemini vision) with loading state
 *   3. Estimate result + contact capture
 *
 * Uses /api/photo-estimate (analysis) and /api/photo-estimate/contact
 * (lead capture) backend endpoints.
 */
import { useState } from "react";
import axios from "axios";
import { Upload, X, Sparkles, Loader2, CheckCircle2, Phone, ArrowRight, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import LoadingReferences from "@/components/LoadingReferences";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SERVICES = [
  { value: "zateplenie", label: "Zateplenie fasády" },
  { value: "rekonstrukcia-kupelne", label: "Rekonštrukcia kúpeľne" },
  { value: "rekonstrukcia", label: "Rekonštrukcia bytu / domu" },
  { value: "novostavba", label: "Novostavba na kľúč" },
  { value: "zamkova-dlazba", label: "Zámková dlažba" },
  { value: "ploty", label: "Plot" },
  { value: "omietky", label: "Omietky a maľby" },
  { value: "ine", label: "Iné" },
];

const EUR = (n) =>
  new Intl.NumberFormat("sk-SK", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);

export default function AiPhotoEstimator({ open, onClose }) {
  const [step, setStep] = useState(1); // 1 form, 2 loading, 3 result, 4 done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [service, setService] = useState("zateplenie");
  const [area, setArea] = useState("");
  const [note, setNote] = useState("");
  const [result, setResult] = useState(null);
  const [contact, setContact] = useState({ name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Nahrajte prosím obrázok.");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const analyze = async () => {
    if (!file) return toast.error("Nahrajte prosím fotku.");
    setBusy(true);
    setStep(2);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("service", service);
      fd.append("area", area);
      fd.append("note", note);
      // Step 1 — server saves photo, enqueues LLM job, returns 202 in <2 s.
      const { data: enq } = await axios.post(`${API}/photo-estimate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60_000,
      });
      const leadId = enq.lead_id;
      // Step 2 — poll status every 2 s (up to ~4 min).
      const started = Date.now();
      const poll = async () => {
        const { data } = await axios.get(`${API}/photo-estimate/status/${leadId}`);
        if (data.ai_status === "done" || data.price_min != null) {
          setResult(data);
          setStep(3);
          return;
        }
        if (data.ai_status === "error") {
          throw new Error(data.ai_error || "AI analýza zlyhala.");
        }
        if (Date.now() - started > 4 * 60 * 1000) {
          throw new Error("Analýza trvá príliš dlho, skúste znova.");
        }
        await new Promise((r) => setTimeout(r, 2000));
        await poll();
      };
      await poll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "AI analýza zlyhala, skúste znova.");
      setStep(1);
    } finally {
      setBusy(false);
    }
  };

  const submitContact = async () => {
    if (!contact.name || !contact.phone) return toast.error("Meno + telefón sú povinné.");
    setBusy(true);
    try {
      // Strip empty optional fields so backend EmailStr doesn't reject '' with 422.
      const email = contact.email?.trim();
      await axios.post(`${API}/photo-estimate/contact`, {
        lead_id: result.lead_id,
        name: contact.name,
        phone: contact.phone,
        ...(email ? { email } : {}),
        consent: true,
      });
      setStep(4);
      // Mark this browser as converted so ExitIntent + repeat CTAs stop nagging.
      try {
        localStorage.setItem("sm_estimator_submitted", "1");
      } catch {
        /* ignore */
      }
      // Notify engagement tracker
      window.__smTrack?.("photo_estimate_submit", contact.phone);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Uloženie zlyhalo.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setArea("");
    setNote("");
    setResult(null);
    setContact({ name: "", phone: "", email: "" });
  };
  const handleClose = () => {
    reset();
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleClose}
      data-testid="ai-photo-estimator"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-[#00E5FF]/30 rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-[0_20px_80px_rgba(0,229,255,0.15)]"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center">
              <Sparkles size={16} className="text-[#00E5FF]" />
            </div>
            <div>
              <h3 className="font-black text-lg tracking-tight">AI Foto-Odhad Ceny</h3>
              <p className="text-[11px] text-zinc-500">
                Nezáväzne · zdarma · výsledok za ~30 s
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
            data-testid="estimator-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Steps */}
        <div className="px-6 py-6 space-y-4">
          {/* STEP 1: form */}
          {step === 1 && (
            <>
              <div>
                <label className="block text-xs text-zinc-400 mb-2">Fotka priestoru*</label>
                <label className="flex flex-col items-center justify-center bg-black/40 border-2 border-dashed border-white/15 hover:border-[#00E5FF]/40 rounded-xl p-6 cursor-pointer transition-colors">
                  {preview ? (
                    <img src={preview} alt="" className="max-h-48 rounded-lg" />
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-full bg-[#00E5FF]/10 flex items-center justify-center mb-2">
                        <Upload size={18} className="text-[#00E5FF]" />
                      </div>
                      <div className="text-sm font-semibold">Kliknutím vyberte fotku</div>
                      <div className="text-[11px] text-zinc-500 mt-1">JPG, PNG · max 12 MB</div>
                    </>
                  )}
                  <input type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="estimator-file" />
                </label>
                {file && (
                  <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1">
                    <ImageIcon size={11} /> {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-2">Aká práca vás zaujíma?*</label>
                <select
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm"
                  data-testid="estimator-service"
                >
                  {SERVICES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-2">Plocha / rozsah (napr. 145 m²)</label>
                <input
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm"
                  placeholder="Napr. 145 m² fasády / 12 m² kúpeľňa"
                  data-testid="estimator-area"
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-2">Poznámka (voliteľné)</label>
                <textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm"
                  placeholder="Napr. chcem sivú omietku, terasa má 20 m²…"
                  data-testid="estimator-note"
                />
              </div>

              <button
                onClick={analyze}
                disabled={!file || busy}
                className="w-full flex items-center justify-center gap-2 bg-[#00E5FF] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black py-3.5 rounded-xl transition-all"
                data-testid="estimator-analyze"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                Zistiť odhad ceny <ArrowRight size={16} />
              </button>
              <p className="text-[11px] text-zinc-500 text-center">
                🔒 Fotku uchováme len na účel odhadu · nezdieľame · GDPR-compliant
              </p>
            </>
          )}

          {/* STEP 2: loading */}
          {step === 2 && (
            <div className="py-6">
              <div className="flex flex-col items-center justify-center text-center">
                <div className="relative w-20 h-20 mb-4">
                  <div className="absolute inset-0 rounded-full border-4 border-[#00E5FF]/20"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-[#00E5FF] border-t-transparent animate-spin"></div>
                  <Sparkles size={24} className="absolute inset-0 m-auto text-[#00E5FF]" />
                </div>
                <div className="font-bold">AI analyzuje vašu fotku…</div>
                <p className="text-xs text-zinc-500 mt-2">Cca 20-40 sekúnd</p>
              </div>
              <LoadingReferences />
            </div>
          )}

          {/* STEP 3: result */}
          {step === 3 && result && (
            <>
              <div className="bg-gradient-to-br from-[#00E5FF]/10 to-transparent border border-[#00E5FF]/30 rounded-xl p-5">
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-black mb-1">
                  Odhadovaná cena
                </div>
                <div className="text-4xl font-black tracking-tight" data-testid="estimator-result-price">
                  {EUR(result.price_min)} <span className="text-zinc-500">–</span> {EUR(result.price_max)}
                </div>
                {result.duration && (
                  <div className="text-sm text-zinc-400 mt-2">
                    ⏱️ Predpokladané trvanie: <span className="text-white font-semibold">{result.duration}</span>
                  </div>
                )}
                {result.summary && (
                  <p className="text-sm text-zinc-300 mt-3 leading-relaxed">{result.summary}</p>
                )}
              </div>

              {result.considerations?.length > 0 && (
                <div>
                  <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2 font-bold">Čo AI zistila</div>
                  <ul className="space-y-1.5">
                    {result.considerations.map((c, i) => (
                      <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                        <CheckCircle2 size={14} className="text-[#00E5FF] flex-shrink-0 mt-0.5" /> {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border-t border-white/5 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-[#FF5E00] font-black mb-2">
                  Chcete presnú ponuku do 24h?
                </div>
                <p className="text-xs text-zinc-500 mb-3">
                  Náš expert vám zavolá s upresnenou kalkuláciou. Zdarma, nezáväzne.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  <input
                    value={contact.name}
                    onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    placeholder="Meno*"
                    aria-label="Meno"
                    autoComplete="name"
                    className="bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm"
                    data-testid="estimator-name"
                  />
                  <input
                    value={contact.phone}
                    onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    placeholder="Telefón*"
                    aria-label="Telefón"
                    autoComplete="tel"
                    inputMode="tel"
                    className="bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm"
                    data-testid="estimator-phone"
                  />
                </div>
                <input
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  placeholder="Email (voliteľné)"
                  aria-label="Email"
                  autoComplete="email"
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm mb-3"
                  data-testid="estimator-email"
                />
                <button
                  onClick={submitContact}
                  disabled={busy}
                  className="w-full flex items-center justify-center gap-2 bg-[#FF5E00] hover:brightness-110 text-black font-black py-3.5 rounded-xl transition-all disabled:opacity-40"
                  data-testid="estimator-submit-contact"
                >
                  <Phone size={16} /> Chcem presnú ponuku
                </button>
              </div>
            </>
          )}

          {/* STEP 4: done */}
          {step === 4 && (
            <div className="py-10 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <CheckCircle2 size={28} className="text-emerald-400" />
              </div>
              <div className="text-xl font-black">Ďakujeme!</div>
              <p className="text-sm text-zinc-400 mt-2">
                Ozveme sa vám do <strong className="text-white">24 hodín</strong> s presnou kalkuláciou.
              </p>
              <button
                onClick={handleClose}
                className="mt-6 px-6 py-2.5 bg-white/5 hover:bg-white/10 rounded-lg text-sm font-semibold"
                data-testid="estimator-done"
              >
                Zavrieť
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
